<template>
  <div class="iha-issue-page page-wrapper">
    <div class="container-fluid">
      <div class="iha-issue_headercontainer d-flex align-items-end justify-content-between flex-wrap gap-2">
        <div class="left-content">
          <breadcrumb :items="breadcrumbItems" />
          <div class="page-title">
            <h1>Securus Issue Center</h1>
          </div>
        </div>

        <div class="right-content">
          <button class="univ-btn py-2 px-3 d-flex align-items-center gap-1 m-0" @click="openRaiseIhaIssueModal(null)">
            <span class="d-flex align-items-center justify-content-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                <path fill="currentColor" d="M19 12.998h-6v6h-2v-6H5v-2h6v-6h2v6h6z" />
              </svg>
            </span>
            Add New Issue
          </button>
        </div>
      </div>

      <div class="row iha-issue-card">
        <div class="issue-tabs">
          <div class="tab-item" :class="{ active: activeTab === 'all' }" @click="setTab('all')">
            All <span class="count">{{ stats.total_issues }}</span>
          </div>

          <div class="tab-item" :class="{ active: activeTab === 'pending' }" @click="setTab('pending')">
            Pending <span class="count">{{ stats.open_issues }}</span>
          </div>

          <div class="tab-item" :class="{ active: activeTab === 'progress' }" @click="setTab('progress')">
            In Progress
            <span class="count">{{ stats.in_progress_issues }}</span>
          </div>

          <div class="tab-item" :class="{ active: activeTab === 'resolved' }" @click="setTab('resolved')">
            Resolved <span class="count">{{ stats.closed_issues }}</span>
          </div>

          <div class="tab-item" :class="{ active: activeTab === 'withdrawn' }" @click="setTab('withdrawn')">
            Withdrawn <span class="count">{{ stats.withdrawn_issues }}</span>
          </div>
        </div>

        <!-- FILTERS -->
        <div class="issue-filters">
          <QuickFilterInput class="filter-search" :labels="['Member Name', 'Plan']" :placeholders="{
            'Member Name': 'Enter Member Name',
            Plan: 'Enter Plan Name',
          }" :preserveInputOnLabelChange="true" @search="handleQuickSearch" ref="quickFilter" />

          <QuickDropdownFilter label="Issue Type" :options="issueTypeOptions" :model-value="issueType" @update:modelValue="issueType = $event; fetchIssues()" />

          <div class="date-range-wrapper">
            <div class="fw-semibold">Date:</div>
            <input type="date" v-model="startDate" class="date-input" />
          </div>

          <!-- CLEAR -->
          <button class="clear-btn d-flex align-items-center gap-1" @click="clearFilters">
            <span class="icon-wrap d-flex align-items-center justify-content-center">
              <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24">
                <path fill="currentColor"
                  d="M18.36 19.78L12 13.41l-6.36 6.37l-1.42-1.42L10.59 12L4.22 5.64l1.42-1.42L12 10.59l6.36-6.36l1.41 1.41L13.41 12l6.36 6.36z" />
              </svg>
            </span>
            Clear
          </button>
        </div>
        <div class="top-pagination p-0">
          <IhaPagination :rows="rows" @page-change="handlePageChange" />
        </div>
        <!-- TABLE -->
        <div class="table-responsive p-0">
          <table class="table issue-table">
            <thead>
              <tr>
                <th>Issue Type</th>
                <th>Member</th>
                <th>Plan</th>
                <th>Description</th>
                <th>Submitted On</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>

            <tbody>
              <tr v-for="issue in filteredIssues" :key="issue.id">
                <td>
                  <span class="status-badge-issue issue-type" :class="issue.type">{{
                    issue.type
                  }}</span>
                </td>

                <td>
                  <div class="member-name fw-semibold">{{ issue.member_details?.cfname }} {{ issue.member_details?.clname
                    }}</div>
                  <div class="member-email">{{ issue.member_details?.cemail }}</div>
                </td>

                <td>{{ issue.plan_details?.web_display_name || '-' }}</td>

                <td>
                  <div class="desc" >
                    {{ issue.details }}
                  </div>
                </td>

                <td>{{ formatDate(issue.created_at) }}</td>

                <td>
                  <div class="d-flex flex-column gap-2 align-items-start">
                    <span class="status-badge-issue" :class="getIssueStatus(issue.status)">
                      {{ getIssueStatus(issue.status) }}
                    </span>
                    <div v-if="getIssueStatus(issue.status) === 'pending' && issue.admin_seen_status"
                      class="viewed-by-iha status-badge-issue d-flex align-items-center gap-1">
                      <div class="icon-wrap">
                        <i class="fas fa-eye"></i>
                      </div>

                      <span class="ms-1">Viewed by IHA</span>
                    </div>
                  </div>
                </td>

                <td class="action-icons">
                  <custom-button title="View Details" iconClass="fas fa-info" @onClicked="viewDetail(issue.id)" />
                  <custom-button title="Withdraw Issue" v-if="getIssueStatus(issue.status) === 'pending'"
                    class="sign-incomplete" iconClass="fas fa-ban" @onClicked="openWithdrawModal(issue)" />
                </td>
              </tr>

              <tr v-if="isLoading">
                <td colspan="7" class="text-center">
                  <div class="spinner-border" role="status">
                    <span class="visually-hidden">Loading...</span>
                  </div>
                </td>
              </tr>
              <tr v-if="!isLoading && filteredIssues.length === 0">
                <td colspan="7" class="text-center">No issues found</td>
              </tr>
            </tbody>
          </table>
        </div>
      </div>
    </div>
    <AddIhaIssue :isOpen="showRaiseIhaIssueModal" :row="row" :issue-types="issueTypes" @close="showRaiseIhaIssueModal = false"
      @created="fetchIssues(); fetchStats()" />
    <WithdrawIssueModal v-if="showWithdrawIssueModal" :row="row" @close="showWithdrawIssueModal = false"
      @withdraw="handleWithdraw" />
  </div>
</template>

<script>
import QuickFilterInput from "../../components/ui/QuickFilterInput.vue";
import QuickDropdownFilter from "../../components/ui/QuickDropdownFilter.vue";
import AddIhaIssue from "./AddIhaIssue.vue";
import IhaPagination from "../../components/ui/IhaPagination.vue";
import WithdrawIssueModal from "./WithdrawIssueModal.vue";
import "./IhaIssue.css";
import axios from "axios";
import Helper from '@/resource/Helper';

export default {
  components: {
    QuickFilterInput,
    QuickDropdownFilter,
    AddIhaIssue,
    WithdrawIssueModal,
    IhaPagination,
  },

  data() {
    return {
      breadcrumbItems: [{ title: "Securus Issue Center", active: true }],
      row: null,
      rows: {},
      activeTab: "all",

      quickFilterLabel: "",
      quickFilterValue: "",

      issueType: "All",
      issueTypes: [],

      startDate: "",
      endDate: "",
      showRaiseIhaIssueModal: false,
      showWithdrawIssueModal: false,

      issues: [],
      isLoading: false,
      stats: {
        total_issues: 0,
        open_issues: 0,
        closed_issues: 0,
        in_progress_issues: 0,
        withdrawn_issues: 0,
      },
    };
  },

  created() {
    this.fetchIssues();
    this.fetchIssueTypes();
    this.fetchStats();
  },

  watch: {
    startDate() {
      this.fetchIssues();
    }
  },

  computed: {
    filteredIssues() {
      return this.issues;
    },
    issueTypeOptions() {
      return ["All", ...this.issueTypes];
    },
  },

  methods: {
    formatDate(dateString) {
      if (!dateString) return '';
      const date = new Date(dateString);
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, '0');
      const day = String(date.getDate()).padStart(2, '0');
      return `${year}-${month}-${day}`;
    },
    
    getIssueStatus(statusId) {
      const statusMap = {
        4: 'pending',
        5: 'withdrawn',
        6: 'seen',
        7: 'progress',
        8: 'resolved'
      };
      return statusMap[statusId] || 'Unknown';
    },

    getStatusId(statusString) {
      const statusMap = {
        pending: 4,
        withdrawn: 5,
        seen: 6,
        progress: 7,
        resolved: 8,
      };
      return statusMap[statusString.toLowerCase()];
    },

    async fetchIssues(page = 1, pageSize = 10) {
      this.isLoading = true;
      try {
        const params = {
          agent_id: this.$store.state.Auth.rep.id,
          page,
          per_page: pageSize,
        };
        if (this.activeTab !== 'all') {
          params.status = this.getStatusId(this.activeTab);
        }

        if (this.issueType !== 'All' && this.issueType !== 'all') {
          params.type = this.issueType;
        }
        if (this.startDate) {
          params.date = this.startDate;
        }
        if (this.quickFilterValue) {
          if (this.quickFilterLabel === 'Member Name') {
            params.member_id = this.quickFilterValue;
          }
          if (this.quickFilterLabel === 'Plan') {
            params.plan_id = this.quickFilterValue;
          }
        }

        const apiUrl = `${process.env.VUE_APP_TICKETING_URL_API}v2/iha/agent-tickets`;
        const response = await axios.get(apiUrl, {
          headers: {
            'X-Ticketing-Token': Helper.ticketingToken(),
          },
          params: params,
        });
        this.rows = response.data.data || {};
        this.issues = this.rows.data || [];
      } catch (error) {
        console.error("Error fetching issues:", error);
      } finally {
        this.isLoading = false;
      }
    },

    async fetchIssueTypes() {
      try {
        const apiUrl = `${process.env.VUE_APP_TICKETING_URL_API}v2/iha/issue-types`;
        const response = await axios.get(apiUrl, {
          headers: {
            'X-Ticketing-Token': Helper.ticketingToken(),
          },
        });

        this.issueTypes = response.data.data;
      } catch (error) {
        console.error("Error fetching issue types:", error);
      }
    },

    async fetchStats() {
      try {
        const apiUrl = `${process.env.VUE_APP_TICKETING_URL_API}v2/iha/stats?agent_id=${this.$store.state.Auth.rep.id}`;
        const response = await axios.get(apiUrl, {
          headers: {
            'X-Ticketing-Token': Helper.ticketingToken(),
          },
        });
        this.stats = response.data.data;
      } catch (error) {
        console.error("Error fetching stats:", error);
      }
    },

    viewDetail(id) {
      const route = this.$router.resolve({
        name: "IhaIssueDetail",
        params: { id: id }
      });
      window.open(route.href, '_blank');
    },

    openRaiseIhaIssueModal(row) {
      this.row = row;
      this.showRaiseIhaIssueModal = true;
    },

    openWithdrawModal(issue) {
      this.row = issue;
      this.showWithdrawIssueModal = true;
    },

    async handleWithdraw(issueId) {
      try {
        const apiUrl = `${process.env.VUE_APP_TICKETING_URL_API}v2/agent/issues/change-status`;
        await axios.post(apiUrl, {
          id: issueId,
          status: 5, // 5 for withdrawn
          agent_id: this.$store.state.Auth.rep.id,
        }, {
          headers: {
            'X-Ticketing-Token': Helper.ticketingToken(),
          },
        });
        this.$toast.success("The issue has been withdrawn successfully.");
        this.fetchIssues();
        this.fetchStats();
      } catch (error) {
        this.$toast.error(error?.response?.data?.message || "Failed to withdraw issue.");
        console.error("Error withdrawing issue:", error);
      }
    },

    setTab(tab) {
      this.activeTab = tab;
      this.fetchIssues(1, this.rows.per_page || 10);
      this.fetchStats();
    },

    handleQuickSearch(data) {
      this.quickFilterLabel = data.label;
      this.quickFilterValue = data.value.toLowerCase();
      this.fetchIssues(1, this.rows.per_page || 10);
    },

    clearFilters() {
      this.quickFilterLabel = "";
      this.quickFilterValue = "";
      this.issueType = "All";
      this.startDate = "";
      this.endDate = "";
      this.$refs.quickFilter?.clearInputValues();
      
      this.fetchIssues(1, this.rows.per_page || 10);
    },

    handlePageChange({ page, pageSize }) {
      this.fetchIssues(page, pageSize);
    },

    countStatus(status) {
      return this.issues.filter((i) => this.getIssueStatus(i.status) === status).length;
    },
  },
};
</script>

<style scoped></style>
